import {
  Button,
  Card,
  CreditCardIcon,
  ErrorAlert,
  Input,
  SwitchInput,
  danger,
  success,
  useCustomForm,
  useFetchData,
} from "@";
import IntegrationShell from "../shared/IntegrationShell";
import { getPaymentSettings, savePaymentSettings } from "../shared/api";
import { defaultValues, schema } from "./utils/validation";

const PROVIDERS = [
  {
    key: "myfatoorah",
    name: "MyFatoorah",
    description: "بوابة شاملة للخليج — مدى، Apple Pay، فيزا، تحويل بنكي.",
  },
  {
    key: "paymob",
    name: "Paymob",
    description: "حلول دفع شائعة في الشرق الأوسط مع iframe جاهز.",
  },
  {
    key: "moyasar",
    name: "Moyasar",
    description: "بوابة سعودية حديثة بواجهة مطوّر بسيطة.",
  },
];

const COUNTRY_OPTIONS = [
  { id: "SAU", name: "السعودية" },
  { id: "ARE", name: "الإمارات" },
  { id: "KWT", name: "الكويت" },
  { id: "EGY", name: "مصر" },
  { id: "QAT", name: "قطر" },
  { id: "BHR", name: "البحرين" },
  { id: "OMN", name: "عُمان" },
  { id: "JOR", name: "الأردن" },
];

const Index = () => {
  const {
    reset,
    watch,
    errors,
    setValue,
    register,
    handleSubmit,
    isSubmitting,
  } = useCustomForm(defaultValues, schema);

  const { error, fetchData } = useFetchData({
    reset,
    fun: getPaymentSettings,
  });

  const provider = watch("provider");

  const submit = async (data) => {
    try {
      await savePaymentSettings(data);
      success("تم حفظ إعدادات بوابة الدفع");
    } catch (err) {
      danger("تعذّر حفظ الإعدادات. تحقق من القيم وحاول مجدداً.");
    }
  };

  return (
    <>
      <ErrorAlert reload={fetchData} show={error} />

      <IntegrationShell
        eyebrow="الإعدادات / التكاملات"
        title="بوابات الدفع"
        icon={CreditCardIcon}
        breadcrumbName="بوابات الدفع"
        providers={PROVIDERS}
        active={provider}
        onActiveChange={(key) => setValue("provider", key)}
        isSaving={isSubmitting}
      >
        <form onSubmit={handleSubmit(submit)} className="mt-2">
          {provider === "myfatoorah" && (
            <Card title="إعدادات MyFatoorah">
              <div className="grid gap-5 md:grid-cols-2">
                <Input
                  star
                  type="password"
                  label="API Token"
                  placeholder="rLtt6JWvbUHDDhsZnfpAhpYk4..."
                  error={errors.myfatoorahApiKey}
                  input={register("myfatoorahApiKey")}
                />
                <Input
                  star
                  type="select"
                  label="الدولة"
                  list={COUNTRY_OPTIONS}
                  value={watch("myfatoorahCountry")}
                  change={(v) => setValue("myfatoorahCountry", v)}
                  error={errors.myfatoorahCountry}
                />
                <div className="md:col-span-2">
                  <Input
                    type="textarea"
                    rows={3}
                    label="Webhook Secret"
                    placeholder="اختياري — يُستخدم للتحقق من إشعارات الدفع"
                    error={errors.myfatoorahWebhookSecret}
                    input={register("myfatoorahWebhookSecret")}
                  />
                </div>
                <SwitchInput
                  isBordered
                  label="وضع الإنتاج (Live)"
                  description="عطّله لاستخدام البيئة التجريبية أثناء التطوير."
                  className="md:col-span-2"
                  checked={watch("myfatoorahIsLive")}
                  change={(v) => setValue("myfatoorahIsLive", v)}
                />
              </div>
            </Card>
          )}

          {provider === "paymob" && (
            <Card title="إعدادات Paymob">
              <div className="grid gap-5 md:grid-cols-2">
                <Input
                  star
                  type="password"
                  label="API Key"
                  placeholder="ZXlKaGJHY2lPaUpJVXpVeE1pSXNJ..."
                  error={errors.paymobApiKey}
                  input={register("paymobApiKey")}
                />
                <Input
                  star
                  label="Integration ID"
                  placeholder="123456"
                  error={errors.paymobIntegrationId}
                  input={register("paymobIntegrationId")}
                />
                <Input
                  star
                  label="iFrame ID"
                  placeholder="65432"
                  error={errors.paymobIframeId}
                  input={register("paymobIframeId")}
                />
                <Input
                  type="password"
                  label="HMAC Secret"
                  placeholder="اختياري — للتحقق من الـ callback"
                  error={errors.paymobHmacSecret}
                  input={register("paymobHmacSecret")}
                />
                <SwitchInput
                  isBordered
                  label="وضع الإنتاج (Live)"
                  description="عطّله لاستخدام بيئة الـ test."
                  className="md:col-span-2"
                  checked={watch("paymobIsLive")}
                  change={(v) => setValue("paymobIsLive", v)}
                />
              </div>
            </Card>
          )}

          {provider === "moyasar" && (
            <Card title="إعدادات Moyasar">
              <div className="grid gap-5 md:grid-cols-2">
                <Input
                  star
                  label="Publishable Key"
                  placeholder="pk_test_..."
                  error={errors.moyasarPublishableKey}
                  input={register("moyasarPublishableKey")}
                />
                <Input
                  star
                  type="password"
                  label="Secret Key"
                  placeholder="sk_test_..."
                  error={errors.moyasarSecretKey}
                  input={register("moyasarSecretKey")}
                />
                <div className="md:col-span-2">
                  <Input
                    type="textarea"
                    rows={3}
                    label="Webhook Secret"
                    placeholder="اختياري — لتوقيع الـ webhooks"
                    error={errors.moyasarWebhookSecret}
                    input={register("moyasarWebhookSecret")}
                  />
                </div>
                <SwitchInput
                  isBordered
                  label="وضع الإنتاج (Live)"
                  description="استخدم مفاتيح الإنتاج بدل مفاتيح الاختبار."
                  className="md:col-span-2"
                  checked={watch("moyasarIsLive")}
                  change={(v) => setValue("moyasarIsLive", v)}
                />
              </div>
            </Card>
          )}

          <div className="mt-5 flex items-center justify-end gap-3">
            <p className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">
              سيُستخدم المزوّد المختار في كل عمليات الدفع الجديدة.
            </p>
            <Button
              type="submit"
              title="حفظ الإعدادات"
              isLoading={isSubmitting}
            />
          </div>
        </form>
      </IntegrationShell>
    </>
  );
};

export default Index;
